// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * render.ts — draw the tower.
 *
 * Canvas 2D over a camera that pans up as the tower climbs. The renderer owns
 * the juice that is purely visual — particles, screen shake, the goal glow, the
 * wind streaks — and reads engine-neutral brick geometry (world-px quads) off
 * the Game, so it never touches the physics engine directly.
 *
 * A hidden tab never fires rAF, so nothing about correctness lives here; the
 * runner steps the sim on a fixed accumulator regardless.
 */

import { Game, PLAY_W } from './game';
import * as P from './palette';

interface Particle { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; r: number; }
interface Streak { y: number; dir: number; life: number; }

export interface Renderer {
  resize(): void;
  draw(game: Game, dtMs: number, opts?: { vignette?: number }): void;
  shake(mag: number): void;
  burst(x: number, y: number, color: string, n: number, spread?: number): void;
  windStreak(dir: number): void;
  setReducedMotion(b: boolean): void;
  /** world→screen, for hit-testing the aim drag from the runner if needed. */
  screenToWorldX(clientX: number, rectLeft: number): number;
  worldScale(): number;
}

export function createRenderer(canvas: HTMLCanvasElement): Renderer {
  const ctx = canvas.getContext('2d')!;
  let cssW = 0;
  let cssH = 0;
  let dpr = 1;
  let scale = 1;
  let camTopY = 0;         // world y at the top edge of the canvas
  let camInit = false;
  let shakeAmt = 0;
  let reduced = false;

  const particles: Particle[] = [];
  const streaks: Streak[] = [];

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    // Guard against a transient 0-size measurement (a NaN scale silently drops
    // input and paints nothing).
    if (rect.width < 2 || rect.height < 2) return;
    cssW = rect.width;
    cssH = rect.height;
    dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    scale = cssW / (PLAY_W * 1.06);
  }

  function worldScale(): number { return scale; }

  function screenToWorldX(clientX: number, rectLeft: number): number {
    const sx = clientX - rectLeft;
    return (sx - cssW / 2) / scale + PLAY_W / 2;
  }

  function w2sx(wx: number): number { return (wx - PLAY_W / 2) * scale + cssW / 2; }
  function w2sy(wy: number): number { return (wy - camTopY) * scale; }

  function updateCamera(game: Game): void {
    const viewH = cssH / scale;
    const c = game.worldConst();
    // focus: the higher of the active brick / tower top, with headroom above.
    let topY = game.topWorldY();
    const guide = game.activeGuide();
    if (guide) topY = Math.min(topY, guide.y - c.CELL);
    const focusY = topY - 2 * c.CELL;
    // Plinth framed at ~76% down when short; follow the tower once it is tall.
    const byPlinth = c.PLINTH_TOP_Y - 0.76 * viewH;
    const byFocus = focusY - 0.14 * viewH;
    const target = Math.min(byPlinth, byFocus);
    if (!camInit) { camTopY = target; camInit = true; }
    else camTopY += (target - camTopY) * (reduced ? 1 : 0.12);
  }

  function drawBackground(): void {
    const g = ctx.createLinearGradient(0, 0, 0, cssH);
    g.addColorStop(0, P.SKY_TOP);
    g.addColorStop(1, P.SKY_BOTTOM);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cssW, cssH);
  }

  function drawPlinth(game: Game): void {
    const c = game.worldConst();
    const x = w2sx(PLAY_W / 2 - c.plinthW / 2);
    const y = w2sy(c.PLINTH_TOP_Y);
    const w = c.plinthW * scale;
    const h = (c.PLINTH_BOTTOM_Y - c.PLINTH_TOP_Y) * scale;
    ctx.fillStyle = P.PLINTH;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = P.PLINTH_EDGE;
    ctx.fillRect(x, y + h - Math.max(2, 3 * scale), w, Math.max(2, 3 * scale));
    // top lip highlight
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.fillRect(x, y, w, Math.max(1, 2 * scale));
  }

  function drawLines(game: Game): void {
    const c = game.worldConst();
    // goal line (Ascent)
    const goal = game.goalCells();
    if (goal > 0) {
      const gy = w2sy(c.PLINTH_TOP_Y - goal * c.CELL);
      if (gy > -20 && gy < cssH + 20) {
        ctx.save();
        ctx.strokeStyle = P.GOAL;
        ctx.globalAlpha = 0.9;
        ctx.lineWidth = Math.max(1.5, 2 * scale);
        ctx.setLineDash([10, 8]);
        ctx.beginPath();
        ctx.moveTo(w2sx(6), gy);
        ctx.lineTo(w2sx(PLAY_W - 6), gy);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.font = `${Math.round(12)}px system-ui, sans-serif`;
        ctx.fillStyle = P.GOAL;
        ctx.textAlign = 'left';
        ctx.fillText('GOAL', w2sx(8), gy - 5);
        ctx.restore();
      }
    }
    // kill line — subtle warning strip below the plinth
    const ky = w2sy(c.KILL_Y);
    if (ky > -20 && ky < cssH + 20) {
      ctx.save();
      ctx.strokeStyle = P.KILL;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(0, ky);
      ctx.lineTo(cssW, ky);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  function drawStreaks(): void {
    for (const s of streaks) {
      const a = Math.max(0, s.life);
      ctx.save();
      ctx.globalAlpha = a * 0.5;
      ctx.strokeStyle = P.GUST;
      ctx.lineWidth = 2;
      const y = w2sy(s.y);
      ctx.beginPath();
      const x0 = s.dir > 0 ? 0 : cssW;
      const x1 = s.dir > 0 ? cssW : 0;
      ctx.moveTo(x0, y);
      ctx.lineTo(x1, y);
      ctx.stroke();
      ctx.restore();
    }
  }

  function drawBricks(game: Game): void {
    const polys = game.brickPolys();
    ctx.lineJoin = 'round';
    for (const br of polys) {
      ctx.fillStyle = P.brickColor(br.tint);
      ctx.strokeStyle = P.BRICK_LINE;
      ctx.lineWidth = Math.max(1.2, 1.6 * scale);
      // the live falling brick reads a touch brighter so it's easy to track
      ctx.globalAlpha = br.active && !br.settled ? 0.97 : 1;
      for (const quad of br.quads) {
        ctx.beginPath();
        ctx.moveTo(w2sx(quad[0].x), w2sy(quad[0].y));
        for (let i = 1; i < quad.length; i++) ctx.lineTo(w2sx(quad[i].x), w2sy(quad[i].y));
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawPreview(game: Game): void {
    // A soft vertical guide under the falling brick, pointing at the landing
    // zone. The brick itself is drawn by drawBricks (it is a live body now).
    const guide = game.activeGuide();
    if (!guide) return;
    const c = game.worldConst();
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.16)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 6]);
    ctx.beginPath();
    ctx.moveTo(w2sx(guide.x), w2sy(guide.y));
    ctx.lineTo(w2sx(guide.x), w2sy(c.PLINTH_TOP_Y));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  function updateParticles(dtMs: number): void {
    const dt = dtMs / 1000;
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.life -= dtMs;
      if (p.life <= 0) { particles.splice(i, 1); continue; }
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 420 * dt; // gravity on dust
      p.vx *= 0.98;
    }
    for (let i = streaks.length - 1; i >= 0; i--) {
      streaks[i].life -= dtMs / 480;
      if (streaks[i].life <= 0) streaks.splice(i, 1);
    }
  }

  function drawParticles(): void {
    for (const p of particles) {
      const a = Math.max(0, p.life / p.max);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      const sx = w2sx(p.x);
      const sy = w2sy(p.y);
      const r = p.r * scale * (0.5 + a * 0.5);
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(0.6, r), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  function draw(game: Game, dtMs: number, opts?: { vignette?: number }): void {
    if (cssW < 2) { resize(); if (cssW < 2) return; }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    updateCamera(game);
    updateParticles(dtMs);

    let ox = 0;
    let oy = 0;
    if (shakeAmt > 0.3 && !reduced) {
      ox = (Math.random() * 2 - 1) * shakeAmt;
      oy = (Math.random() * 2 - 1) * shakeAmt;
      shakeAmt *= 0.86;
    } else shakeAmt = 0;

    drawBackground();
    ctx.save();
    ctx.translate(ox, oy);
    drawLines(game);
    drawPlinth(game);
    drawBricks(game);
    drawParticles();
    drawPreview(game);
    drawStreaks(); // wind streaks, drawn last for emphasis
    ctx.restore();

    if (opts?.vignette && opts.vignette > 0 && !reduced) {
      const v = Math.min(1, opts.vignette);
      const g = ctx.createRadialGradient(cssW / 2, cssH / 2, cssH * 0.3, cssW / 2, cssH / 2, cssH * 0.75);
      g.addColorStop(0, 'rgba(224,106,120,0)');
      g.addColorStop(1, `rgba(224,106,120,${0.28 * v})`);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, cssW, cssH);
    }
  }

  function shake(mag: number): void { if (!reduced) shakeAmt = Math.min(22, shakeAmt + mag); }

  function burst(x: number, y: number, color: string, n: number, spread = 60): void {
    if (reduced) n = Math.min(n, 3);
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = Math.random() * spread + 20;
      particles.push({
        x, y,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - 30,
        life: 380 + Math.random() * 260,
        max: 640,
        color,
        r: 1.2 + Math.random() * 1.8,
      });
    }
  }

  function windStreak(dir: number): void {
    for (let i = 0; i < 5; i++) {
      streaks.push({ y: camTopY + (cssH / scale) * (0.15 + Math.random() * 0.6), dir, life: 1 });
    }
  }

  function setReducedMotion(b: boolean): void { reduced = b; }

  return { resize, draw, shake, burst, windStreak, setReducedMotion, screenToWorldX, worldScale };
}